import React, { useEffect, useRef, useState } from "react";
import { C, T, L, M, label } from "@/lib/foundationTheme";

/**
 * Foundation marketing brand primitives — "Title Plan".
 *
 * Everything the rebrand repeats lives here: the red boundary line, the grid
 * cross field, the sheet headers that open each section, the button set and
 * the card. Pages compose these rather than re-drawing the linework, which is
 * how the seventeen marketing templates stay one drawing set instead of
 * seventeen near-misses.
 */

/* ------------------------------------------------------------------ motion */

/** Honours the OS setting once, at mount — nothing here needs to react live. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const q = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(q.matches);
    const on = (e) => setReduced(e.matches);
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, []);
  return reduced;
}

/**
 * Fires once when the element first reaches the viewport. Used for the
 * self-drawing lines and the fade-ups, both of which play a single time —
 * a page that re-animates on every scroll pass reads as a demo, not a product.
 */
export function useInView(options) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: "-10% 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen, options]);
  return [ref, seen];
}

/** 12px fade-up on first view. The only entrance on the site. */
export function Reveal({ children, delay = 0, as: Tag = "div", style, ...rest }) {
  const reduced = usePrefersReducedMotion();
  const [ref, seen] = useInView();
  return (
    <Tag
      ref={ref}
      style={{
        opacity: reduced || seen ? 1 : 0,
        transform: reduced || seen ? "none" : "translateY(12px)",
        transition: reduced ? "none" : `opacity ${M.enter} ${M.ease} ${delay}ms, transform ${M.enter} ${M.ease} ${delay}ms`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------- the red line */

/**
 * THE SIGNATURE DEVICE.
 *
 * On a title plan the red line marks exactly what you own, so every asset the
 * page wants to claim gets registered inside one. The edges run half a degree
 * off-parallel so it reads drawn rather than generated, each vertex carries a
 * survey cross, and the whole thing draws itself once on first view.
 *
 * scale: "hero"   — around the product screenshot, 2px, 20px offset
 *        "inline" — around a headline phrase or a price, 2px, 10px offset
 *        "micro"  — a single edge, used as the active-nav underline
 *
 * Rendered as an overlay: it never affects layout, so wrapping content in one
 * can't shift the grid.
 */
export function RedLine({ scale = "inline", color = C.red, offset, children, className, style }) {
  const reduced = usePrefersReducedMotion();
  const [ref, seen] = useInView();
  const [box, setBox] = useState(null);
  const stroke = scale === "hero" ? 2 : scale === "inline" ? 2 : 1.5;
  const pad = offset ?? (scale === "hero" ? 20 : 10);

  /**
   * The polygon is drawn in real pixels, from a measured box, rather than in a
   * 0–100 viewBox stretched with preserveAspectRatio="none".
   *
   * That stretch was the bug: a dash pattern is laid out along the path in USER
   * units, then scaled by a different factor on each axis, so a single
   * dasharray could not cover all four edges evenly — the horizontals drew with
   * gaps while the verticals never appeared. Measuring costs one observer per
   * device and makes the geometry exact, including the off-parallel edges,
   * which are the whole character of the mark.
   */
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setBox({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  const w = (box?.w ?? 0) + pad * 2;
  const h = (box?.h ?? 0) + pad * 2;
  // Each corner is nudged a fraction of a degree off true so no two edges run
  // parallel — a steady hand, not a generated rectangle.
  const inset = stroke / 2;
  const pts = box
    ? [
        [inset + 1.5, inset + 0.8],
        [w - inset - 0.4, inset],
        [w - inset, h - inset - 1.2],
        [inset, h - inset],
      ].map((p) => p.join(",")).join(" ")
    : "";
  const perimeter = box ? 2 * (w + h) : 0;

  return (
    <span
      ref={ref}
      className={className}
      style={{ position: "relative", display: scale === "inline" ? "inline-block" : "block", ...style }}
    >
      {children}
      {box && (
        <svg
          aria-hidden="true"
          width={w}
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          style={{ position: "absolute", top: -pad, left: -pad, pointerEvents: "none", overflow: "visible" }}
        >
          <polygon
            points={pts}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="butt"
            strokeLinejoin="miter"
            style={{
              strokeDasharray: perimeter,
              strokeDashoffset: reduced || seen ? 0 : perimeter,
              transition: reduced ? "none" : `stroke-dashoffset ${M.draw} ${M.ease} 300ms`,
            }}
          />
        </svg>
      )}
      {/* Survey crosses mark the vertices — the registration marks on a plan.
          They land after the line has finished drawing. */}
      {box && [[0, 0], [1, 0], [1, 1], [0, 1]].map(([x, y]) => (
        <Cross
          key={`${x}${y}`}
          style={{
            position: "absolute",
            left: x ? `calc(100% + ${pad}px)` : -pad,
            top: y ? `calc(100% + ${pad}px)` : -pad,
            transform: "translate(-50%, -50%)",
            opacity: reduced || seen ? 0.4 : 0,
            transition: reduced ? "none" : `opacity 300ms ${M.ease} 1100ms`,
          }}
        />
      ))}
    </span>
  );
}

/** The registration mark: an 8px '+' in ink. Marks rule ends, vertices, corners. */
export function Cross({ size = 8, color = C.ink, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 8 8" aria-hidden="true" style={{ pointerEvents: "none", ...style }}>
      {/* fill="none" is load-bearing: an SVG <path> fills black by default, and
          Tailwind's preflight does not reset it. */}
      <path d="M4 0V8M0 4H8" fill="none" stroke={color} strokeWidth="1" />
    </svg>
  );
}

/**
 * The 96px field of survey crosses behind paper sections. Pure texture — one
 * SVG pattern, no DOM cost, sits under everything.
 */
export function GridField({ opacity = 0.12, color = C.ink, style }) {
  const id = React.useId();
  return (
    <svg
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", ...style }}
    >
      <defs>
        <pattern id={`grid-${id}`} width="96" height="96" patternUnits="userSpaceOnUse">
          <path d="M48 42V54M42 48H54" stroke={color} strokeWidth="1" opacity={opacity} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#grid-${id})`} />
    </svg>
  );
}

/**
 * The nav's bottom edge, and any rule that wants to read as a map scale bar:
 * a hairline carrying ink ticks every 8px.
 */
export const scaleRule = {
  backgroundImage: `repeating-linear-gradient(to right, ${C.ink}1A 0 1px, transparent 1px 8px)`,
  backgroundPosition: "bottom",
  backgroundSize: "100% 4px",
  backgroundRepeat: "repeat-x",
  borderBottom: `1px solid ${C.hairline}`,
};

/* ----------------------------------------------------------------- headings */

/**
 * The mono overline: a 16px red rule, then letterspaced caps. Opens every
 * block on the site that isn't a full sheet header.
 */
export function Overline({ children, tone = "light", rule = true, style, ...rest }) {
  const c = tone === "dark" ? "rgba(242,239,231,0.65)" : C.secondary;
  const r = tone === "dark" ? C.signal : C.red;
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 12, ...label(11), color: c, ...style }}
      {...rest}
    >
      {rule && <span aria-hidden="true" style={{ width: 16, height: 2, background: r, flexShrink: 0 }} />}
      {children}
    </div>
  );
}

/**
 * Every section opens like a numbered drawing sheet: a full-width rule with the
 * sheet number at the left, the title beside it, and the sheet count right.
 * This is the device that makes fourteen different templates feel like one
 * document set.
 */
export function SheetHeader({ number, title, annotation, tone = "light", style }) {
  const dark = tone === "dark";
  const line = dark ? C.hairlineDark : C.hairline;
  const dim = dark ? "rgba(242,239,231,0.65)" : C.secondary;
  const solid = dark ? C.paper : C.ink;
  return (
    <div style={{ borderTop: `1px solid ${line}`, paddingTop: 16, marginBottom: 48, ...style }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 20 }}>
          {number && <span style={{ ...label(11), color: dim }}>{number}</span>}
          <h2 style={{ ...T.titleStyle, fontSize: 22, color: solid, margin: 0 }}>{title}</h2>
        </div>
        {annotation && <span style={{ ...label(11), color: dim }}>{annotation}</span>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ buttons */

/**
 * Three buttons, one shape. Labels are always mono caps — the surveyor's hand
 * signing off the action.
 *
 * Red is never a button fill. On the primary it is the movement: a 2px bar
 * sweeping the bottom edge on hover. That keeps the red budget honest and
 * keeps a red rectangle from ever reading as a destructive control.
 */
export function Button({ variant = "primary", as: Tag = "button", children, style, ...rest }) {
  const [hover, setHover] = useState(false);
  const base = {
    ...label(12),
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 24px",
    borderRadius: L.radius,
    border: "1px solid transparent",
    cursor: "pointer",
    textDecoration: "none",
    position: "relative",
    overflow: "hidden",
    transition: `background ${M.hover} ${M.ease}, color ${M.hover} ${M.ease}`,
    ...style,
  };

  if (variant === "tertiary") {
    return (
      <Tag
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          ...base,
          padding: 0,
          border: "none",
          background: "none",
          color: hover ? C.red : C.ink,
        }}
        {...rest}
      >
        {children}
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            transform: hover ? "translateX(4px)" : "none",
            transition: `transform ${M.hover} ${M.ease}`,
          }}
        >
          →
        </span>
      </Tag>
    );
  }

  const secondary = variant === "secondary";
  return (
    <Tag
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...base,
        background: secondary ? (hover ? C.surface : "transparent") : hover ? "#24221F" : C.ink,
        color: secondary ? C.ink : C.paper,
        borderColor: secondary ? C.ink : C.ink,
      }}
      {...rest}
    >
      {children}
      {!secondary && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            height: 2,
            width: hover ? "100%" : 0,
            background: C.signal,
            transition: `width ${M.hover} ${M.ease}`,
          }}
        />
      )}
    </Tag>
  );
}

/* -------------------------------------------------------------------- cards */

/**
 * White card on paper ground, hairline border, no shadow. `marks` adds the
 * drafting crop-marks; `trace` makes a red boundary trace the perimeter on
 * hover, which is the card-scale echo of the hero's red line.
 */
export function Card({ children, marks = false, trace = false, style, ...rest }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: C.surface,
        border: `1px solid ${hover && trace ? C.red : C.hairline}`,
        borderRadius: L.radius,
        padding: 32,
        transition: `border-color ${M.hover} ${M.ease}`,
        ...style,
      }}
      {...rest}
    >
      {marks && [[0, 0], [1, 0], [1, 1], [0, 1]].map(([x, y]) => (
        <svg
          key={`${x}${y}`}
          width="8" height="8" viewBox="0 0 8 8" aria-hidden="true"
          style={{
            position: "absolute",
            left: x ? "auto" : 8, right: x ? 8 : "auto",
            top: y ? "auto" : 8, bottom: y ? 8 : "auto",
            opacity: 0.2,
            transform: `scale(${x ? -1 : 1}, ${y ? -1 : 1})`,
          }}
        >
          <path d="M0 0H8M0 0V8" stroke={C.ink} strokeWidth="1" />
        </svg>
      ))}
      {children}
    </div>
  );
}

/**
 * The dark register — manifesto and closing CTA only, twice per page at most.
 * Carries hand-drawn contour loops at 7% so it reads as a survey sheet's
 * reverse rather than a generic dark band.
 */
export function InkPanel({ children, contours = true, style, ...rest }) {
  return (
    <section style={{ position: "relative", background: C.panel, color: C.paper, overflow: "hidden", ...style }} {...rest}>
      {contours && (
        <svg
          aria-hidden="true"
          viewBox="0 0 1200 600"
          preserveAspectRatio="xMidYMid slice"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.07, pointerEvents: "none" }}
        >
          <g fill="none" stroke={C.paper} strokeWidth="1">
            <path d="M-60 420C160 380 300 300 470 318s250 120 430 92 300-120 420-96" />
            <path d="M-60 470C170 428 320 348 500 366s240 122 420 94 300-122 400-98" />
            <path d="M-60 520C180 476 340 396 530 414s230 124 410 96 300-124 380-100" />
            <path d="M-40 130C130 96 260 60 400 74s210 78 350 62 240-64 350-50" />
            <path d="M-40 182C140 146 280 108 420 122s200 80 340 64 250-66 360-52" />
            <path d="M240 250C340 236 420 218 520 226s130 44 210 36" />
          </g>
        </svg>
      )}
      <div style={{ position: "relative" }}>{children}</div>
    </section>
  );
}

/** Page-width wrapper. Every section uses it, so the 1200px grid never drifts. */
export function Container({ children, style, ...rest }) {
  return (
    <div style={{ maxWidth: L.maxWidth, margin: "0 auto", padding: "0 24px", ...style }} {...rest}>
      {children}
    </div>
  );
}

/**
 * The straight-on browser frame. No tilt, no perspective, no laptop bezel —
 * the product is shown as it is, which is the whole argument of the direction.
 */
export function Frame({ src, alt, caption, shadow = false, style, ...rest }) {
  return (
    <figure style={{ margin: 0, ...style }} {...rest}>
      <div
        style={{
          border: `1px solid ${C.hairline}`,
          borderRadius: L.radius,
          background: C.surface,
          overflow: "hidden",
          boxShadow: shadow ? L.heroShadow : "none",
        }}
      >
        <div
          style={{
            height: 28,
            borderBottom: `1px solid ${C.hairline}`,
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "0 12px",
          }}
        >
          {[0, 1, 2].map((i) => (
            <span key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: `${C.ink}33` }} />
          ))}
        </div>
        <img src={src} alt={alt} style={{ display: "block", width: "100%" }} loading="lazy" />
      </div>
      {caption && (
        <figcaption style={{ ...label(11), color: C.secondary, marginTop: 12 }}>{caption}</figcaption>
      )}
    </figure>
  );
}

export default {
  RedLine, Cross, GridField, Overline, SheetHeader,
  Button, Card, InkPanel, Container, Frame, Reveal, scaleRule,
};
